import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserCog, ArrowLeft, Trash2, Save, Plus, ShieldCheck, Lock, Mail } from "lucide-react";
import { LoginInfo } from "@/components/LoginInfo";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TOOLKITS, toolkitTitle } from "@/lib/toolkits";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const BLANK = { email: "", role: "manager", scopeAll: true, toolkits: [] };

export default function AdminUsers() {
  usePageMeta({ title: "Admin · Users | Southern Black Girls Toolkits", description: "Staff dashboard." });
  const { user, loading: authLoading } = useAuth();

  if (authLoading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  if (!user || !user.is_admin)
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteNav />
        <div className="mx-auto max-w-md px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-black text-sbg-burgundy">Admins only</h1>
          <p className="mt-3 text-sbg-burgundy/70">Only full admins can manage users and roles.</p>
          <div className="mt-6"><Link to="/admin" className="rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Back to admin</Link></div>
        </div>
        <SiteFooter />
      </div>
    );

  return <Manager me={user.email} />;
}

function Manager({ me }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setRows(data);
    } catch {
      toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (u) => {
    setForm({
      email: u.email,
      role: u.role,
      scopeAll: u.toolkit_scope === "all",
      toolkits: Array.isArray(u.toolkit_scope) ? u.toolkit_scope : [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleToolkit = (slug) => {
    setForm((f) => ({
      ...f,
      toolkits: f.toolkits.includes(slug) ? f.toolkits.filter((s) => s !== slug) : [...f.toolkits, slug],
    }));
  };

  const save = async () => {
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address."); return; }
    const toolkit_scope = form.role === "admin" ? "all" : form.scopeAll ? "all" : form.toolkits;
    if (form.role === "manager" && !form.scopeAll && form.toolkits.length === 0) {
      toast.error("Pick at least one toolkit, or choose All."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/users/${encodeURIComponent(email)}`, { role: form.role, toolkit_scope });
      if (data.invite_sent === true) toast.success(`${email} saved — invitation email sent.`);
      else if (data.invite_sent === false) toast.warning(`${email} saved, but the invitation email could not be sent. They can still sign in with their email.`);
      else toast.success(`${email} saved.`);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that user.");
    } finally {
      setSaving(false);
    }
  };

  const [inviting, setInviting] = useState(null);
  const resendInvite = async (email) => {
    setInviting(email);
    try {
      await api.post(`/admin/users/${encodeURIComponent(email)}/resend-invite`);
      toast.success(`Invitation sent to ${email}.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the invitation right now.");
    } finally {
      setInviting(null);
    }
  };

  const remove = async (email) => {
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success(`${email} removed.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove that user.");
    }
  };

  const scopeLabel = (u) =>
    u.toolkit_scope === "all" ? "All toolkits" : (u.toolkit_scope || []).map(toolkitTitle).join(", ") || "—";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-5xl px-5 py-10">
        <Link to="/admin" data-testid="users-back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-pt-purple text-white">
            <UserCog className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-black text-sbg-burgundy" data-testid="users-page-title">Users &amp; roles</h1>
            <p className="text-sm text-sbg-burgundy/60">Admins have full access. Managers do everything except manage users, limited to their toolkits.</p>
          </div>
        </div>

        {/* Add / edit form */}
        <div className="mt-8 rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10" data-testid="user-form-card">
          <h2 className="font-display text-lg font-bold text-sbg-burgundy">{form.email && rows.some((r) => r.email === form.email.trim().toLowerCase()) ? "Edit user" : "Add a user"}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-sbg-burgundy">Email</span>
              <input
                data-testid="user-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="person@example.com"
                className="w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-sbg-fuchsia"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-sbg-burgundy">Role</span>
              <select
                data-testid="user-role-select"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-sbg-fuchsia"
              >
                <option value="manager">Manager — full access except managing users</option>
                <option value="admin">Admin — full access including users</option>
              </select>
            </label>
          </div>

          {form.role === "manager" && (
            <div className="mt-4 rounded-2xl bg-sbg-blush/30 p-4" data-testid="user-scope-block">
              <span className="block text-sm font-bold text-sbg-burgundy">Toolkit access</span>
              <label className="mt-2 flex items-center gap-2 text-sm font-semibold text-sbg-burgundy">
                <input data-testid="scope-all-radio" type="radio" name="scope" checked={form.scopeAll} onChange={() => setForm((f) => ({ ...f, scopeAll: true }))} className="h-4 w-4 accent-sbg-fuchsia" />
                All toolkits (current &amp; future)
              </label>
              <label className="mt-1.5 flex items-center gap-2 text-sm font-semibold text-sbg-burgundy">
                <input data-testid="scope-specific-radio" type="radio" name="scope" checked={!form.scopeAll} onChange={() => setForm((f) => ({ ...f, scopeAll: false }))} className="h-4 w-4 accent-sbg-fuchsia" />
                Specific toolkits
              </label>
              {!form.scopeAll && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {TOOLKITS.map((t) => (
                    <button
                      key={t.slug}
                      type="button"
                      data-testid={`scope-toolkit-${t.slug}`}
                      onClick={() => toggleToolkit(t.slug)}
                      className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${form.toolkits.includes(t.slug) ? "bg-sbg-fuchsia text-white" : "bg-white text-sbg-burgundy ring-1 ring-sbg-raspberry/20"}`}
                    >
                      {t.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button data-testid="user-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save user
            </button>
            {form.email && (
              <button onClick={() => setForm(BLANK)} className="rounded-full bg-sbg-blush px-5 py-2.5 text-sm font-bold text-sbg-raspberry">Clear</button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="mt-6 overflow-x-auto rounded-3xl bg-white ring-1 ring-sbg-raspberry/10" data-testid="users-list">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sbg-raspberry" /></div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-sbg-blush/50 text-xs uppercase tracking-wider text-sbg-raspberry">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Toolkits</th>
                  <th className="px-4 py-3">Last login</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.email} data-testid={`user-row-${u.email}`} className="border-t border-sbg-raspberry/10">
                    <td className="px-4 py-3 font-semibold text-sbg-burgundy">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${u.role === "admin" ? "bg-pt-lavender/30 text-pt-purple" : "bg-sky-100 text-sky-700"}`}>
                        {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : null}{u.role}
                      </span>
                      {u.has_password && (
                        <span data-testid={`user-password-badge-${u.email}`} title="Has set a password" className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200">
                          <Lock className="h-3 w-3" /> Password
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sbg-burgundy/70">{scopeLabel(u)}</td>
                    <td className="px-4 py-3"><LoginInfo email={u.email} last={u.last_login} count={u.login_count} testId={`login-info-${u.email}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button data-testid={`user-invite-${u.email}`} onClick={() => resendInvite(u.email)} disabled={inviting === u.email} title="Resend invitation email" className="inline-flex items-center gap-1.5 rounded-full bg-sbg-turquoise px-3 py-1.5 text-xs font-bold text-sbg-burgundy disabled:opacity-60">
                          {inviting === u.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Resend invite
                        </button>
                        {u.env ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-sbg-burgundy/50"><Lock className="h-3.5 w-3.5" /> Owner (locked)</span>
                        ) : (
                          <>
                            <button data-testid={`user-edit-${u.email}`} onClick={() => edit(u)} className="rounded-full bg-sbg-burgundy/80 px-3 py-1.5 text-xs font-bold text-white">Edit</button>
                            {u.email !== me && (
                              <button data-testid={`user-remove-${u.email}`} onClick={() => setConfirm({ title: "Remove user?", description: `${u.email} will lose their role and access. You can add them again anytime.`, confirmLabel: "Remove user", onConfirm: () => remove(u.email) })} className="rounded-full bg-sbg-blush px-2.5 py-1.5 text-sbg-raspberry"><Trash2 className="h-3.5 w-3.5" /></button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => { confirm?.onConfirm?.(); setConfirm(null); }}
      />
      <SiteFooter />
    </div>
  );
}
